import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@learn-me/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { LoginRequest } from "@learn-me/types";

interface LocationState {
  email?: string;
  redirectTo?: string;
}

export default function LoginPage() {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const { login, loginLoading, isAuthenticated, user } = useAuth();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginRequest>();

  // Pre-llenar email si viene de invitación
  useEffect(() => {
    if (state?.email) {
      setValue("email", state.email);
    }
  }, [state?.email, setValue]);

  if (isAuthenticated) {
    // Si hay redirectTo, ir allí; sino, ir al dashboard o admin
    const defaultRedirect = user?.role === "ADMIN" ? "/admin" : "/dashboard";
    const redirectTo = state?.redirectTo || defaultRedirect;
    return <Navigate to={redirectTo} replace />;
  }

  const onSubmit = (data: LoginRequest) => {
    login(data);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <h1 className="text-2xl font-bold">Iniciar Sesion</h1>
          <p className="text-muted-foreground">
            Ingresa tus credenciales para acceder
          </p>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <Input
              label="Correo electronico"
              type="email"
              placeholder="tu@email.com"
              error={errors.email?.message}
              {...register("email", {
                required: "El correo es requerido",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Correo invalido",
                },
              })}
            />
            <Input
              label="Contrasena"
              type="password"
              placeholder="********"
              error={errors.password?.message}
              {...register("password", {
                required: "La contrasena es requerida",
                minLength: {
                  value: 8,
                  message: "Minimo 8 caracteres",
                },
              })}
            />
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                Olvidaste tu contrasena?
              </Link>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" isLoading={loginLoading}>
              Iniciar Sesion
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              No tienes cuenta?{" "}
              <Link to="/register" className="text-primary hover:underline">
                Registrate
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
